// Author: Ahsan Saleem
/**
 * Session memory — BloomOS restores where you left off. Persists the active
 * workspace, the last route, recent objects and per-route scroll so a reload or
 * fresh sign-in returns you to your context, not to a blank home.
 */
export type Workspace = 'operate' | 'observe' | 'analyse' | 'commerce' | 'system';

// `cap` is the capability a role needs to see this app; it mirrors the API's
// authorization so the nav shows only what the signed-in role can actually use
// (the API remains the enforcement boundary). No `cap` means always visible.
export interface AppDef { to: string; label: string; glyph: string; cap?: string; }
export interface WorkspaceDef { id: Workspace; label: string; short: string; apps: AppDef[]; }

// The V2 experience is the whole app: every destination is a /v2/* route. Legacy
// bare paths (/pulse, /stock, …) still resolve — App.tsx redirects them here.
export const WORKSPACES: WorkspaceDef[] = [
  { id: 'operate', label: 'Operate', short: '1', apps: [
    { to: '/v2', label: 'Pulse', glyph: 'pulse', cap: 'read' },
    { to: '/v2/work', label: 'Today', glyph: 'today', cap: 'read' },
    { to: '/v2/record', label: 'Record', glyph: 'record', cap: 'ops.write' },
  ] },
  { id: 'observe', label: 'Observe', short: '2', apps: [
    { to: '/v2/canvas', label: 'Facility', glyph: 'facility', cap: 'read' },
    { to: '/v2/alerts', label: 'Alerts', glyph: 'alerts', cap: 'read' },
    { to: '/v2/rules', label: 'Rules', glyph: 'alerts', cap: 'control.approve' },
    { to: '/v2/maintenance', label: 'Maintenance', glyph: 'maintenance', cap: 'read' },
    { to: '/v2/compliance', label: 'Compliance', glyph: 'trace', cap: 'read' },
  ] },
  { id: 'analyse', label: 'Analyse', short: '3', apps: [
    { to: '/v2/crops', label: 'Crops', glyph: 'grow', cap: 'read' },
    { to: '/v2/production', label: 'Production', glyph: 'grow', cap: 'read' },
    { to: '/v2/trace', label: 'Traceability', glyph: 'trace', cap: 'read' },
    { to: '/v2/reports', label: 'Reports', glyph: 'reports', cap: 'read' },
  ] },
  { id: 'commerce', label: 'Commerce', short: '4', apps: [
    { to: '/v2/partners', label: 'Suppliers & customers', glyph: 'commerce', cap: 'commercial.read' },
    { to: '/v2/catalog', label: 'Catalog', glyph: 'catalog', cap: 'commercial.read' },
    { to: '/v2/stock', label: 'Stock', glyph: 'stock', cap: 'inventory.read' },
    { to: '/v2/procurement', label: 'Procurement', glyph: 'supply', cap: 'procurement.read' },
    { to: '/v2/receiving', label: 'Receiving', glyph: 'receiving', cap: 'procurement.read' },
    { to: '/v2/supply', label: 'Supplier bills', glyph: 'billing', cap: 'procurement.read' },
    { to: '/v2/trade', label: 'Sales & delivery', glyph: 'sales', cap: 'sales.read' },
    { to: '/v2/tax', label: 'Tax & GST', glyph: 'tax', cap: 'tax.read' },
    { to: '/v2/accounting', label: 'Accounting bridge', glyph: 'accounting', cap: 'accounting.read' },
  ] },
  { id: 'system', label: 'System', short: '5', apps: [
    { to: '/v2/edge', label: 'Edge', glyph: 'edge', cap: 'read' },
    { to: '/v2/onboard', label: 'Add facility', glyph: 'facility', cap: 'admin' },
    // The Simulation Console injects fault scenarios into a site's telemetry — a
    // development/demo tool, never something to point at a live facility. Present
    // in local dev only; the production build (import.meta.env.DEV === false) omits it.
    ...(import.meta.env.DEV ? ([{ to: '/simulation', label: 'Simulation', glyph: 'sim', cap: 'admin' }] as AppDef[]) : []),
    { to: '/v2/settings', label: 'Settings', glyph: 'system' },
  ] },
];

/** Whether a role's capabilities (from /me) satisfy `cap`. Fails open while the
 *  capability set is still loading — the API stays the real gate. */
export function hasCap(caps: string[] | '*' | undefined, cap: string): boolean {
  if (caps === '*') return true;
  if (!caps) return true;
  return caps.includes(cap);
}

/** The workspaces + apps the signed-in role should see, empty workspaces dropped. */
export function visibleWorkspaces(caps: string[] | '*' | undefined): WorkspaceDef[] {
  return WORKSPACES
    .map((w) => ({ ...w, apps: w.apps.filter((a) => !a.cap || hasCap(caps, a.cap)) }))
    .filter((w) => w.apps.length > 0);
}

const PATH_WS = new Map<string, Workspace>();
for (const w of WORKSPACES) for (const a of w.apps) PATH_WS.set(a.to, w.id);
export function workspaceOf(path: string): Workspace {
  // V2 destinations are two-segment (/v2/stock); match the full path, then the
  // first two segments, then fall back to the home workspace.
  return PATH_WS.get(path) ?? PATH_WS.get('/' + path.split('/').slice(1, 3).join('/')) ?? 'operate';
}

export interface RecentObject { kind: 'zone' | 'batch' | 'alert' | 'asset'; id: string; label: string; sub?: string; path: string; at: number; }

interface SessionState {
  workspace: Workspace;
  path: string;
  recents: RecentObject[];
  scroll: Record<string, number>;
  /** Inspector dock width (px) — the operator sets it once, the OS remembers it. */
  inspectorW: number;
  /** Inspector kept open across navigation (a pinned object follows you). */
  inspectorPinned: boolean;
}
const KEY = 'bloomos_session';
const DEFAULT: SessionState = { workspace: 'operate', path: '/v2', recents: [], scroll: {}, inspectorW: 460, inspectorPinned: false };
export const INSPECTOR_MIN = 380;
export const INSPECTOR_MAX = 720;

function read(): SessionState {
  try { return { ...DEFAULT, ...(JSON.parse(localStorage.getItem(KEY) || '{}') as Partial<SessionState>) }; }
  catch { return { ...DEFAULT }; }
}
function write(s: SessionState): void { localStorage.setItem(KEY, JSON.stringify(s)); }

export const session = {
  get: read,
  workspace: () => read().workspace,
  lastPath: () => read().path,
  setPath(path: string) { const s = read(); s.path = path; s.workspace = workspaceOf(path); write(s); },
  setWorkspace(w: Workspace) { const s = read(); s.workspace = w; write(s); },
  recents: () => read().recents,
  addRecent(o: Omit<RecentObject, 'at'>) {
    const s = read();
    s.recents = [{ ...o, at: Date.now() }, ...s.recents.filter((r) => !(r.kind === o.kind && r.id === o.id))].slice(0, 8);
    write(s);
  },
  setScroll(path: string, y: number) { const s = read(); s.scroll[path] = y; write(s); },
  getScroll(path: string) { return read().scroll[path] ?? 0; },
  inspectorWidth: () => Math.min(INSPECTOR_MAX, Math.max(INSPECTOR_MIN, read().inspectorW)),
  setInspectorWidth(px: number) { const s = read(); s.inspectorW = Math.min(INSPECTOR_MAX, Math.max(INSPECTOR_MIN, Math.round(px))); write(s); },
  inspectorPinned: () => read().inspectorPinned,
  setInspectorPinned(on: boolean) { const s = read(); s.inspectorPinned = on; write(s); },
};
